import React, { useEffect, useRef, useState } from 'react';
import { ApiDocParam } from '@/types/ApiDocs.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { Copy, Check } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-python';

// ─── אייקון דינמי מ-Lucide ────────────────────────────────────────────────
export function DynIcon({ name, className }: { name?: string | null; className?: string }) {
    const Comp = name ? (LucideIcons as Record<string, React.ElementType>)[name] : LucideIcons.Folder;
    return Comp ? <Comp className={className} /> : <LucideIcons.Folder className={className} />;
}

// ─── כפתור העתקה ─────────────────────────────────────────────────────────
export function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <button
            onClick={copy}
            className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
        >
            {copied ? <Check className="size-4 text-green-400" /> : <Copy className="size-4" />}
        </button>
    );
}

// ─── פאנל קוד ────────────────────────────────────────────────────────────
export function CodePanel({ code, language }: { code: string; language: string }) {
    const ref = useRef<HTMLElement>(null);
    useEffect(() => {
        if (ref.current) Prism.highlightElement(ref.current);
    }, [code]);
    return (
        <div className="relative bg-[#1e1e2e] rounded-lg overflow-hidden text-xs">
            <div className="flex items-center justify-between px-3 py-1.5 bg-[#2a2a3a] border-b border-white/10">
                <span className="text-gray-400 text-[11px] uppercase tracking-wide font-sans font-normal">
                    {language}
                </span>
                <CopyButton text={code} />
            </div>
            <pre className="overflow-x-auto p-4 m-0 !bg-transparent" dir="ltr">
                <code
                    ref={ref}
                    className={`language-${language === 'Python' ? 'python' : 'bash'}`}
                >
                    {code}
                </code>
            </pre>
        </div>
    );
}

// ─── שדה פרמטר ───────────────────────────────────────────────────────────
export function ParamField({
    param,
    value,
    onChange,
}: {
    param: ApiDocParam;
    value: string;
    onChange: (v: string) => void;
}) {
    return (
        <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                {param.label}
                {param.required && <span className="text-red-500">*</span>}
                {param.in === 'path' && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                        path
                    </Badge>
                )}
                {param.description && (
                    <span className="text-gray-400 font-normal">— {param.description}</span>
                )}
            </label>
            {param.type === 'select' && param.options ? (
                <Select value={value} onValueChange={onChange}>
                    <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder={param.placeholder || 'בחר...'} />
                    </SelectTrigger>
                    <SelectContent>
                        {param.options.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                                {o.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            ) : (
                <Input
                    type={
                        param.type === 'password'
                            ? 'password'
                            : param.type === 'number'
                              ? 'number'
                              : 'text'
                    }
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={param.placeholder}
                    className="h-8 text-sm"
                />
            )}
        </div>
    );
}
